import { BEIGE_500, BLUE_100, BLUE_600, CORAL_600, GRAY_200, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'

import Box from '@mui/material/Box'
import { FC } from 'react'
import { MeasurementOnSitePropertyType } from 'models/purchaseFlow/measurementDTO'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import apartmentSingleFloorIllustration from 'assets/img/measurement/property_apartment_single_floor.svg'
import houseIllustration from 'assets/img/measurement/property_house.svg'
import multiUnitBuildingIllustration from 'assets/img/measurement/property_multi_unit_building.svg'
import twoFamilyHouseIllustration from 'assets/img/measurement/property_two_family_house.svg'
import { useTranslation } from 'react-i18next'

/** Card definition for each measurement property type, ordered per design (Kat. 1-4).
 * Kat. 1/2 carry a descriptor listing the covered property kinds (replaces the removed house-type pills, per design);
 * Kat. 3/4 descriptors are skipped — they would only repeat the localised card title — and show the per-unit pricing note instead. */
const propertyTypeCards: { type: MeasurementOnSitePropertyType, illustration: string, categoryNumber: number, hasDescription?: boolean, isPricedPerUnit?: boolean }[] = [
  { type: MeasurementOnSitePropertyType.APARTMENT_SINGLE_FLOORS, illustration: apartmentSingleFloorIllustration, categoryNumber: 1, hasDescription: true },
  { type: MeasurementOnSitePropertyType.SINGLE_FAMILY_HOUSE, illustration: houseIllustration, categoryNumber: 2, hasDescription: true },
  { type: MeasurementOnSitePropertyType.SEMI_DETACHED_HOUSE, illustration: twoFamilyHouseIllustration, categoryNumber: 3, isPricedPerUnit: true },
  { type: MeasurementOnSitePropertyType.APARTMENT_BUILDING, illustration: multiUnitBuildingIllustration, categoryNumber: 4, isPricedPerUnit: true }
]

/**
 * @interface Props PropertyTypeCards properties
 */
interface Props {
  /** Currently selected property type */
  selectedType: MeasurementOnSitePropertyType
  /** Called with the property type when a card is clicked */
  onSelect: (type: MeasurementOnSitePropertyType) => void
}

/**
 * @component
 * "What type of property is it?" selector of the redesigned On-Site Area Report step (ISSUE-2187) —
 * 4 illustrated cards for the measurement categories (Kat. 1-4).
 *
 * @example
 * <PropertyTypeCards selectedType={measurementPropertyType} onSelect={setMeasurementPropertyType} />
 */
export const PropertyTypeCards: FC<Props> = ({ selectedType, onSelect }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'measurement_step' })

  return (
    <Box sx={{ backgroundColor: BEIGE_500, borderRadius: '.8rem', padding: '2.4rem' }}>
      <Stack gap="2.4rem" alignItems="center">

        <Typography variant="text-md" color={GRAY_900} fontWeight={600}>{t('property_type_card.title')}</Typography>

        <Box
          display="grid"
          gap=".8rem"
          // fit-content width + fr columns: every card equalises to the widest card's intrinsic width,
          // which is the nowrap title + category row (descriptors are excluded from it via the width-0 trick below)
          sx={{
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(20rem, 1fr))' },
            justifyContent: 'center',
            width: { xs: '100%', sm: 'fit-content' },
            maxWidth: '100%'
          }}
        >
          {propertyTypeCards.map(card => (
            <Stack
              key={card.type}
              gap=".8rem"
              alignItems="center"
              justifyContent="flex-end"
              onClick={() => onSelect(card.type)}
              sx={{
                padding: '1.2rem',
                borderRadius: '.8rem',
                border: `1px solid ${card.type === selectedType ? BLUE_600 : GRAY_400}`,
                backgroundColor: card.type === selectedType ? BLUE_100 : WHITE,
                cursor: 'pointer'
              }}
            >

              <Box component="img" src={card.illustration} alt={t(`property_type_card.${card.type}`)} sx={{ height: '5.8rem', maxWidth: '100%' }} />

              {/* Kept on a single line — the grid sizes the cards so the longest title + category badge always fits */}
              <Stack direction="row" gap=".8rem" alignItems="center" justifyContent="center">

                <Typography variant="text-sm" color={GRAY_900} fontWeight="medium" whiteSpace="nowrap">{t(`property_type_card.${card.type}`)}</Typography>

                <Typography
                  variant="text-xs"
                  color={GRAY_700}
                  sx={{
                    padding: '.2rem .5rem',
                    borderRadius: '.8rem',
                    border: `1px solid ${GRAY_400}`,
                    backgroundColor: GRAY_200,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t('property_type_card.category_badge', { number: card.categoryNumber })}
                </Typography>

              </Stack>

              {/* width: 0 + minWidth: 100% keeps these wrapping lines out of the card's intrinsic width — only the title row drives it */}
              {card.hasDescription &&
                <Typography variant="text-xs" color={GRAY_700} textAlign="center" sx={{ width: 0, minWidth: '100%' }}>
                  {t(`property_type_card.description.${card.type}`)}
                </Typography>
              }

              {card.isPricedPerUnit &&
                <Typography variant="text-xs" color={CORAL_600} fontWeight={500} textAlign="center" sx={{ width: 0, minWidth: '100%' }}>
                  {t('property_type_card.priced_per_unit')}
                </Typography>
              }

            </Stack>
          ))}
        </Box>

      </Stack>
    </Box>
  )
}
